import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useState } from "react";

import AppIcon from "@/common/components/Icons/AppIcon";
import { EntityHelper } from "@/common/helpers/entity";
import { StringHelper } from "@/common/helpers/string";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { RepairSpecDtoAll, RepairSpecDtoAny } from "@/common/mapping/mappers/repairSpec";
import { enumService } from "@/common/services/enum";
import { apiClient } from "@/core/api/ApiClient";
import { RepairSpecDto } from "@/core/api/generated/v1";

import MenuWithTrigger from "../../Menu/MenuWithTrigger";
import BaseEntityInline, {
  BaseEntityInlineInheritableProps,
} from "../components/EntityInline/BaseEntityInline";
import { EntityInlineActionIcon } from "../components/EntityInline/EntityInlineActionIcon";
import { EntityInlineActionIconButton } from "../components/EntityInline/EntityInlineActionIconButton";
import { EntityInlineActions } from "../components/EntityInline/EntityInlineActions";
import { getDetalizationInlineText } from "./RepairSpecDetalizationInline";
import RepairSpecViewModal from "./View/RepairSpecViewModal";

export function getRepairSpecInlineText(data: Nil<RepairSpecDtoAll>) {
  return data
    ? StringHelper.join(
        [
          data.name,
          data.partType && data.damageType && `${data.partType?.name} - ${data.damageType?.name}`,
          data.detalization && getDetalizationInlineText(data.detalization),
          `${enumService.getEnumValueName("RepairType", data.repairType)} repair`,
        ],
        ", ",
        {
          skipEmpty: true,
        },
      )
    : undefined;
}

export interface RepairSpecInlineProps extends BaseEntityInlineInheritableProps {
  entity: Nil<RepairSpecDtoAny>;
  entityId?: string | null;
}

/** Inline brief information about the entity. */
export default function RepairSpecInline({
  entity,
  entityId,
  withActions,
  withMenu,
  viewProps,
  ...otherProps
}: RepairSpecInlineProps) {
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const request = useApiRequest(
    apiClient.repairSpecsApi.apiV1RepairSpecsRepairSpecIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      repairSpecId: entityId!,
    },
    {
      deps: [entityId],
      skip: !entityId || !!entity,
    },
  );
  entity = request?.data || entity;

  const asEntity = EntityHelper.nullIfNotEntity(entity) as Nil<RepairSpecDto>;
  const entityIdComputed = entity?.id || entityId;

  return (
    <>
      <BaseEntityInline
        entity={entity}
        entityId={entityId}
        request={request}
        icon={<AppIcon of='repairSpec' inText />}
        content={getRepairSpecInlineText(entity as Nil<RepairSpecDtoAll>)}
        withActions={withActions}
        withMenu={withMenu}
        actions={
          !withActions ? undefined : (
            <EntityInlineActions>
              <EntityInlineActionIconButton
                onClick={() => setIsViewModalOpen(true)}
                tooltipProps={{ title: "Quick view" }}
              >
                <EntityInlineActionIcon of='quickView' />
              </EntityInlineActionIconButton>
            </EntityInlineActions>
          )
        }
        menu={
          !withMenu ? undefined : (
            <MenuWithTrigger
              withAuthCloseOnClick
              trigger={
                <EntityInlineActionIconButton>
                  <EntityInlineActionIcon of='moreVert' />
                </EntityInlineActionIconButton>
              }
            >
              <MenuItem dense onClick={() => setIsViewModalOpen(true)}>
                <ListItemIcon>
                  <AppIcon of='quickView' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Quick view</ListItemText>
              </MenuItem>
            </MenuWithTrigger>
          )
        }
        {...otherProps}
        tooltipTitle={otherProps?.tooltipTitle || "Repair spec"}
      />

      {(withActions || withMenu) && (
        <>
          {/* View modal */}
          {entityIdComputed && (
            <RepairSpecViewModal
              open={isViewModalOpen}
              onClose={() => setIsViewModalOpen(false)}
              viewProps={{
                repairSpecId: entityIdComputed,
                repairSpec: asEntity,
                onUpdate: viewProps?.onUpdate || null,
                onDelete: viewProps?.onDelete || null,
              }}
            />
          )}
        </>
      )}
    </>
  );
}
